import React from 'react'
import { DASHBOARD_POPPER_Z } from './dashboardLayerZ'
import {
  Box,
  Popper,
  Paper,
  ClickAwayListener,
  Grid,
  Typography,
  Divider,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  Button
} from '@mui/material'

const getMode = (billableSelected) => {
  const billable = Boolean(billableSelected?.billable)
  const nonBillable = Boolean(billableSelected?.nonBillable)

  // Both true or both false ⇒ show billable as selected on the toggle
  if (billable && !nonBillable) return 'billable'
  if (!billable && nonBillable) return 'nonBillable'
  return 'billable'
}

const FilterPopper = (props) => {
  const {
    theme: themeProp,
    open = false,
    anchorRef,
    disabled = false,
    vulnUpgradesLoading = false,

    filtersSelected = {},
    onFilterChange = () => {},
    graphFilters = {},

    billableSelected = null,
    onBillableChange = () => {},

    onToggleOpen = () => {},
    onClear = () => {}
  } = props

  const themeFromHook = useTheme()
  const theme = themeProp || themeFromHook
  const bill = theme.custom.colors.billingDrawer
  const success = theme.palette.success.main

  const anchorEl = anchorRef?.current || null
  const hasBillable = billableSelected && typeof billableSelected === 'object'
  const mode = hasBillable ? getMode(billableSelected) : 'billable'

  const stopIfDisabled = (handler) => (e) => {
    if (disabled) return
    handler(e)
  }

  const handleModeChange = (e, nextMode) => {
    if (disabled) return
    if (!nextMode) return
    if (nextMode !== 'billable' && nextMode !== 'nonBillable') return

    // Send ONE event so state updates don't fight each other
    onBillableChange({ target: { name: 'billableMode', value: nextMode } })
  }

  return (
    <Popper
      open={Boolean(open)}
      anchorEl={anchorEl}
      placement='bottom-end'
      disablePortal={false}
      sx={{ zIndex: DASHBOARD_POPPER_Z }}
    >
      <ClickAwayListener onClickAway={() => onToggleOpen(false)}>
        <Paper
          elevation={10}
          sx={{
            mt: 1,
            width: { xs: 'min(calc(100vw - 32px), 520px)', sm: 520 },
            maxHeight: 420,
            overflow: 'auto',
            borderRadius: 2,
            border: `1px solid ${bill.border}`,
            backgroundColor: bill.surface
          }}
        >
          <Grid container direction='column' rowSpacing={0}>
            <Grid item sx={{ p: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='subtitle2' sx={{ color: bill.text, fontWeight: 800 }}>
                    Issue types
                  </Typography>
                  <Typography variant='caption' sx={{ color: bill.muted, display: 'block' }}>
                    Applies to AI graphs and tooltip calculations.
                  </Typography>
                </Box>
                <Button
                  onClick={() => onClear()}
                  size='small'
                  variant='text'
                  disabled={disabled}
                  sx={{ color: theme.palette.error.light, flexShrink: 0 }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>

            <Divider sx={{ borderColor: bill.border }} />

            <Grid item sx={{ p: 1.5 }}>
              <Typography
                variant='overline'
                sx={{ color: bill.muted, lineHeight: 'normal', display: 'block', mb: 0.75 }}
              >
                Support type
              </Typography>

              <FormControl component='fieldset' variant='standard' sx={{ width: '100%' }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={disabled}
                        name={graphFilters.SUPPORT}
                        checked={Boolean(filtersSelected?.[graphFilters.SUPPORT])}
                        onChange={stopIfDisabled(onFilterChange)}
                        sx={{ '&.Mui-checked': { color: success } }}
                      />
                    }
                    label='Support'
                    sx={{ color: bill.text }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={disabled || vulnUpgradesLoading}
                        name={graphFilters.UPGRADES}
                        checked={Boolean(filtersSelected?.[graphFilters.UPGRADES])}
                        onChange={stopIfDisabled(onFilterChange)}
                        sx={{ '&.Mui-checked': { color: success } }}
                      />
                    }
                    label={vulnUpgradesLoading ? 'Upgrades (loading…)' : 'Upgrades'}
                    sx={{ color: bill.text }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={disabled || vulnUpgradesLoading}
                        name={graphFilters.VULNERABILITIES}
                        checked={Boolean(filtersSelected?.[graphFilters.VULNERABILITIES])}
                        onChange={stopIfDisabled(onFilterChange)}
                        sx={{ '&.Mui-checked': { color: success } }}
                      />
                    }
                    label={vulnUpgradesLoading ? 'Vulnerabilities (loading…)' : 'Vulnerabilities'}
                    sx={{ color: bill.text }}
                  />
                </FormGroup>
              </FormControl>
            </Grid>

            {hasBillable && (
              <>
                <Divider sx={{ borderColor: bill.border }} />

                <Grid item sx={{ p: 1.5 }}>
                  <Typography
                    variant='overline'
                    sx={{ color: bill.muted, lineHeight: 'normal', display: 'block', mb: 0.75 }}
                  >
                    Mode
                  </Typography>

                  <ToggleButtonGroup
                    exclusive
                    value={mode}
                    onChange={handleModeChange}
                    disabled={disabled}
                    fullWidth
                    sx={{
                      backgroundColor: alpha('#000', 0.25),
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: `1px solid ${alpha(bill.border, 0.9)}`,
                      '& .MuiToggleButton-root': {
                        color: bill.text,
                        borderColor: alpha(bill.border, 0.7),
                        textTransform: 'none',
                        fontWeight: 600
                      },
                      '& .MuiToggleButton-root.Mui-selected': {
                        color: bill.text,
                        backgroundColor: alpha(success, 0.35)
                      },
                      '& .MuiToggleButton-root.Mui-selected:hover': {
                        backgroundColor: alpha(success, 0.45)
                      }
                    }}
                  >
                    <ToggleButton value='billable'>Billable</ToggleButton>
                    <ToggleButton value='nonBillable'>Non-billable</ToggleButton>
                  </ToggleButtonGroup>

                  <Typography
                    variant='caption'
                    sx={{
                      display: 'block',
                      mt: 1,
                      color: bill.muted
                    }}
                  >
                    Non-billable switches AI Dollar/SP into deficit mode.
                  </Typography>
                </Grid>
              </>
            )}

            {disabled && (
              <>
                <Divider sx={{ borderColor: bill.border }} />

                <Grid item sx={{ p: 1.5 }}>
                  <Typography variant='caption' sx={{ color: bill.muted }}>
                    Filters are disabled unless the data source is n8n.
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      </ClickAwayListener>
    </Popper>
  )
}

export { FilterPopper }
